const KEYDOWN_INPUT_THRESHOLD_MS = 80;
const MAX_TIMESTAMPS = 2000;

function createSession() {
  return {
    keydownCount: 0,
    backspaceCount: 0,
    pasteCount: 0,
    pastedCharTotal: 0,
    inputWithoutKeydownCount: 0,
    sessionStartTime: null,
    keystrokeTimestamps: [],
    textLengthSnapshots: [],
    largestSingleGrowth: 0,
    largestSingleReplacement: 0, // chars changed in a single input without keydown
    replacementCount: 0, // times content was swapped (not just appended)
    untrustedKeydownCount: 0, // synthetic keydown events (isTrusted === false)
    dropCount: 0, // drag-and-drop text events
    droppedCharTotal: 0,
    blurCount: 0,
    focusCount: 0,
    lastKeydownTime: 0,
    lastTextValue: '', // track actual content for replacement detection
  };
}

function computeIKIs(timestamps) {
  const ikis = [];
  for (let i = 1; i < timestamps.length; i++) {
    ikis.push(timestamps[i] - timestamps[i - 1]);
  }
  return ikis;
}

function average(arr) {
  if (arr.length === 0) return 0;
  return arr.reduce((sum, v) => sum + v, 0) / arr.length;
}

function standardDeviation(arr) {
  if (arr.length < 2) return 0;
  const mean = average(arr);
  const squaredDiffs = arr.map((v) => (v - mean) ** 2);
  return Math.sqrt(squaredDiffs.reduce((sum, v) => sum + v, 0) / arr.length);
}

function computeScore(session, messageLength) {
  if (messageLength === 0) {
    return { score: 1, flags: {} };
  }

  const scores = [];
  const flags = {};

  // 1. Keystroke ratio (weight: 0.30)
  const keystrokeRatio = Math.min(session.keydownCount / messageLength, 1.5);
  const keystrokeScore = Math.min(keystrokeRatio / 1.0, 1);
  scores.push({ value: keystrokeScore, weight: 0.3 });
  if (session.keydownCount === 0) flags.no_keystrokes = true;

  // 2. Paste analysis (weight: 0.20)
  const pasteRatio = session.pastedCharTotal / Math.max(messageLength, 1);
  const pasteScore = Math.max(1 - pasteRatio, 0);
  scores.push({ value: pasteScore, weight: 0.2 });
  if (session.pasteCount > 0) flags.pasted = true;

  // 3. Input-keydown correlation (weight: 0.20)
  const totalInputs = session.textLengthSnapshots.length;
  const suspiciousRatio =
    totalInputs > 0 ? session.inputWithoutKeydownCount / totalInputs : 0;
  const correlationScore = Math.max(1 - suspiciousRatio * 2, 0);
  scores.push({ value: correlationScore, weight: 0.2 });
  if (suspiciousRatio > 0.3) flags.suspicious_input_events = true;

  // 4. Typing rhythm (weight: 0.15)
  if (session.keystrokeTimestamps.length >= 5) {
    const ikis = computeIKIs(session.keystrokeTimestamps);
    const stddev = standardDeviation(ikis);
    const mean = average(ikis);
    const cv = mean > 0 ? stddev / mean : 0;
    const rhythmScore = Math.min(cv / 0.3, 1);
    scores.push({ value: rhythmScore, weight: 0.15 });
  } else {
    scores.push({ value: 0.5, weight: 0.15 });
  }

  // 5. Text growth pattern (weight: 0.10)
  let growthScore;
  if (session.largestSingleGrowth <= 4) {
    growthScore = 1.0;
  } else if (session.largestSingleGrowth <= 10) {
    growthScore = 0.6;
  } else if (session.largestSingleGrowth <= 30) {
    growthScore = 0.3;
  } else {
    growthScore = 0.0;
  }
  scores.push({ value: growthScore, weight: 0.1 });
  if (session.largestSingleGrowth > 20) flags.bulk_insert = true;

  // 6. Content replacement detection (weight: 0.10)
  // Catches translate plugins, AI rewrite tools — content is swapped, not appended
  const replacementScore = session.replacementCount === 0 ? 1.0 : 0.0;
  scores.push({ value: replacementScore, weight: 0.1 });
  if (session.replacementCount > 0) flags.content_replaced = true;

  // 7. Synthetic keystroke detection (weight: 0.05)
  // Extensions that simulate typing dispatch events with isTrusted=false
  const untrustedRatio =
    session.keydownCount > 0
      ? session.untrustedKeydownCount / session.keydownCount
      : 0;
  const trustedScore = Math.max(1 - untrustedRatio * 2, 0);
  scores.push({ value: trustedScore, weight: 0.05 });
  if (session.untrustedKeydownCount > 0) flags.synthetic_keystrokes = true;

  // 8. Drop detection — flag only, no weight (rare but worth logging)
  if (session.dropCount > 0) flags.dropped_text = true;

  // Weighted sum
  const totalWeight = scores.reduce((sum, s) => sum + s.weight, 0);
  const score =
    scores.reduce((sum, s) => sum + s.value * s.weight, 0) / totalWeight;

  return {
    score: Math.round(score * 100) / 100,
    flags,
  };
}

export default (context, inject) => {
  try {
    let session = createSession();
    let textarea = null;
    let attached = false;

    function onKeydown(e) {
      if (!session.sessionStartTime) session.sessionStartTime = Date.now();
      session.keydownCount++;
      if (!e.isTrusted) session.untrustedKeydownCount++;
      if (e.key === 'Backspace' || e.key === 'Delete') session.backspaceCount++;
      session.lastKeydownTime = performance.now();
      if (session.keystrokeTimestamps.length < MAX_TIMESTAMPS) {
        session.keystrokeTimestamps.push(performance.now());
      }
    }

    function onInput() {
      if (!session.sessionStartTime) session.sessionStartTime = Date.now();
      const now = performance.now();
      const currentValue = textarea ? textarea.value : '';
      const currentLength = currentValue.length;
      const isprogrammatic =
        now - session.lastKeydownTime > KEYDOWN_INPUT_THRESHOLD_MS;

      if (isprogrammatic) {
        session.inputWithoutKeydownCount++;

        // Detect content replacement (translate plugins, AI tools)
        // A replacement is when content changes substantially without keystrokes
        // Compare actual text, not just length — a translation swaps content
        const prev = session.lastTextValue;
        if (prev.length > 0 && currentLength > 0) {
          // Count how many chars differ between old and new
          const minLen = Math.min(prev.length, currentLength);
          let commonPrefix = 0;
          while (
            commonPrefix < minLen &&
            prev[commonPrefix] === currentValue[commonPrefix]
          ) {
            commonPrefix++;
          }
          const charsChanged =
            prev.length - commonPrefix + currentLength - commonPrefix;
          if (charsChanged > 5) {
            session.replacementCount++;
            if (charsChanged > session.largestSingleReplacement) {
              session.largestSingleReplacement = charsChanged;
            }
          }
        }
      }

      const prevLength =
        session.textLengthSnapshots.length > 0
          ? session.textLengthSnapshots[session.textLengthSnapshots.length - 1]
          : 0;
      const growth = currentLength - prevLength;
      if (growth > session.largestSingleGrowth) {
        session.largestSingleGrowth = growth;
      }
      session.textLengthSnapshots.push(currentLength);
      session.lastTextValue = currentValue;
    }

    function onPaste(e) {
      session.pasteCount++;
      const text =
        (e.clipboardData || window.clipboardData)?.getData('text') || '';
      session.pastedCharTotal += text.length;
    }

    function onDrop(e) {
      session.dropCount++;
      const text = e.dataTransfer?.getData('text') || '';
      session.droppedCharTotal += text.length;
    }

    function onFocus() {
      session.focusCount++;
    }

    function onBlur() {
      session.blurCount++;
    }

    function detach() {
      if (textarea && attached) {
        textarea.removeEventListener('keydown', onKeydown);
        textarea.removeEventListener('input', onInput);
        textarea.removeEventListener('paste', onPaste);
        textarea.removeEventListener('drop', onDrop);
        textarea.removeEventListener('focus', onFocus);
        textarea.removeEventListener('blur', onBlur);
      }
      attached = false;
      textarea = null;
      session = createSession();
    }

    function attachToTextarea(el) {
      if (attached && textarea === el) return;
      detach();
      textarea = el;
      attached = true;

      el.addEventListener('keydown', onKeydown);
      el.addEventListener('input', onInput);
      el.addEventListener('paste', onPaste);
      el.addEventListener('drop', onDrop);
      el.addEventListener('focus', onFocus);
      el.addEventListener('blur', onBlur);
    }

    // Watch for textarea appearance/disappearance
    const observer = new MutationObserver(() => {
      const el = document.getElementById('msg-textarea');
      if (el && !attached) attachToTextarea(el);
      if (!el && attached) detach();
    });
    observer.observe(document.body, { childList: true, subtree: true });

    // Check immediately
    const el = document.getElementById('msg-textarea');
    if (el) attachToTextarea(el);

    inject('getTypingMetrics', () => {
      const messageLength = textarea ? textarea.value.length : 0;
      const result = computeScore(session, messageLength);
      const ikis = computeIKIs(session.keystrokeTimestamps);

      result.raw = {
        keydown_count: session.keydownCount,
        backspace_count: session.backspaceCount,
        paste_count: session.pasteCount,
        pasted_chars: session.pastedCharTotal,
        input_without_keydown: session.inputWithoutKeydownCount,
        composition_time_ms: session.sessionStartTime
          ? Date.now() - session.sessionStartTime
          : 0,
        message_length: messageLength,
        iki_mean_ms: Math.round(average(ikis)),
        iki_stddev_ms: Math.round(standardDeviation(ikis)),
        iki_min_ms: ikis.length ? Math.round(Math.min(...ikis)) : 0,
        iki_max_ms: ikis.length ? Math.round(Math.max(...ikis)) : 0,
        largest_single_growth: session.largestSingleGrowth,
        replacement_count: session.replacementCount,
        largest_replacement: session.largestSingleReplacement,
        untrusted_keydowns: session.untrustedKeydownCount,
        drop_count: session.dropCount,
        dropped_chars: session.droppedCharTotal,
        blur_count: session.blurCount,
      };

      return result;
    });

    inject('resetTypingSession', () => {
      session = createSession();
    });
  } catch (_) {
    // Silent failure — non-critical feature
  }
};
